import React, { createContext, useContext, useMemo } from 'react';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';

/**
 * HITL-1753 — sets the count on a selected option ("4 cream").
 *
 * Addressed exactly as `SelectMenuItemModifier` addresses the pick: the
 * option, its group, and the group's `pathToItem`.
 */
export type SetModifierQuantity = (
  menuItem: ParsedMenuItem,
  modGroup: ParsedModifierGroup,
  pathToItem: string,
  quantity: number
) => void;

interface ModifierQuantityApi {
  /** `selectIsModifierQuantityEnabled` on the brand menu. */
  isEnabled: boolean;
  setQuantity: SetModifierQuantity;
}

const disabled: ModifierQuantityApi = {
  isEnabled: false,
  setQuantity: () => undefined,
};

const ModifierQuantityContext = createContext<ModifierQuantityApi>(disabled);

/**
 * A context rather than a prop: the option rows sit under `ModGroup`,
 * `RecursiveModGroup`, the repeated-slot panel and `ModGroup`'s memo
 * comparator, and threading one more callback through all of them would touch
 * each for a control that only the 7.x rows draw. Provided by `CartItemDetail`
 * next to `ModGroupOpenProvider`; with the flag off the value is `disabled` and
 * no row reads anything new.
 */
export const ModifierQuantityProvider = ({
  isEnabled,
  setQuantity,
  children,
}: ModifierQuantityApi & { children: React.ReactNode }) => {
  // Stable while the inputs are, so a re-render of the pane does not re-render
  // every row that reads this.
  const value = useMemo(
    () => (isEnabled ? { isEnabled, setQuantity } : disabled),
    [isEnabled, setQuantity]
  );
  return (
    <ModifierQuantityContext.Provider value={value}>
      {children}
    </ModifierQuantityContext.Provider>
  );
};

export const useModifierQuantity = () => useContext(ModifierQuantityContext);
